import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce.js';
import { areasApi, buildingsApi, tagsApi } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';

const MODE_CHIPS = [
  { value: 'smart', label: 'Smart', icon: '🔍' },
  { value: 'building_unit', label: 'Building + Unit', icon: '🏢' },
  { value: 'name', label: 'Owner', icon: '👤' },
  { value: 'phone', label: 'Mobile', icon: '📱' },
] as const;

export interface SearchBarState {
  q: string;
  mode: string;
  area_id: string;
  building: string;
  stage: string;
  tag_id: string;
  has_contact: string;
}

export const EMPTY_SEARCH: SearchBarState = {
  q: '',
  mode: 'smart',
  area_id: '',
  building: '',
  stage: '',
  tag_id: '',
  has_contact: '',
};

interface GlobalSearchBarProps {
  /** If provided, controlled mode — search bar modifies this state directly */
  state?: SearchBarState;
  onChange?: (state: SearchBarState) => void;
  /** Result count to display */
  resultCount?: number;
  /** Whether results are loading */
  isUpdating?: boolean;
}

export function GlobalSearchBar({ state, onChange, resultCount, isUpdating }: GlobalSearchBarProps) {
  const navigate = useNavigate();
  const router = useRouterState();
  const isOnLandlords = router.location.pathname === '/landlords';
  const searchRef = useRef<HTMLInputElement>(null);

  // Local state for when NOT on landlords page (uncontrolled mode)
  const [localState, setLocalState] = useState<SearchBarState>(EMPTY_SEARCH);

  // Use controlled state if provided, otherwise local
  const current = state ?? localState;
  const setCurrent = onChange ?? setLocalState;

  // Keyboard shortcut: / to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const debouncedQ = useDebounce(current.q, 400);

  // Auto-navigate to landlords when user types in search (only from non-landlords pages)
  useEffect(() => {
    if (isOnLandlords || !debouncedQ) return;
    navigate({
      to: '/landlords',
      search: buildSearchParams({ ...current, q: debouncedQ }) as any,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  function handleFilterChange(partial: Partial<SearchBarState>) {
    const next = { ...current, ...partial };
    setCurrent(next);

    // If not on landlords page, navigate there with the filter
    if (!isOnLandlords) {
      navigate({
        to: '/landlords',
        search: buildSearchParams(next) as any,
      });
    }
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isOnLandlords && current.q.trim()) {
      navigate({
        to: '/landlords',
        search: buildSearchParams(current) as any,
      });
    }
  }

  function handleClear() {
    setCurrent({ ...EMPTY_SEARCH });
  }

  // Fetch dropdown data — cached in React Query (staleTime 5min covers sessionStorage equivalent)
  const { data: areasData } = useQuery({
    queryKey: ['areas'],
    queryFn: () => areasApi.list(),
    staleTime: 5 * 60_000,
  });
  const areas = areasData?.items ?? [];

  const { data: buildingsData } = useQuery({
    queryKey: ['buildings-simple', current.area_id],
    queryFn: () => buildingsApi.list(current.area_id ? Number(current.area_id) : undefined),
    staleTime: 5 * 60_000,
  });
  const buildings = buildingsData?.items ?? [];
  const buildingsTotal = buildingsData?.total ?? 0;

  const { data: tagsData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
    staleTime: 5 * 60_000,
  });
  const tags = tagsData?.items ?? [];

  const hasFilters = !!(current.area_id || current.building || current.stage || current.tag_id || current.has_contact);

  return (
    <div className="bg-white border-b border-border">
      {/* Row 1: Filter dropdowns */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-wrap">
        {/* Area */}
        <select
          value={current.area_id}
          onChange={(e) => handleFilterChange({ area_id: e.target.value, building: '' })}
          className="h-10 pl-3 pr-8 rounded-lg border border-border text-sm text-primary-text bg-white focus:outline-none focus:border-primary"
          aria-label="Filter by area"
        >
          <option value="">All Areas</option>
          {areas.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        {/* Building */}
        <select
          value={current.building}
          onChange={(e) => handleFilterChange({ building: e.target.value })}
          className="h-10 pl-3 pr-8 rounded-lg border border-border text-sm text-primary-text bg-white focus:outline-none focus:border-primary"
          aria-label="Filter by building"
        >
          <option value="">All Buildings ({buildingsTotal.toLocaleString()})</option>
          {buildings.map((b) => (
            <option key={b.name} value={b.name}>{b.name}</option>
          ))}
        </select>

        {/* Status */}
        <select
          value={current.stage}
          onChange={(e) => handleFilterChange({ stage: e.target.value })}
          className="h-10 pl-3 pr-8 rounded-lg border border-border text-sm text-primary-text bg-white focus:outline-none focus:border-primary"
          aria-label="Filter by status"
        >
          <option value="">Status</option>
          <option value="available">Available</option>
          <option value="not_available">Not Available</option>
          <option value="sold">Sold</option>
        </select>

        {/* Tags */}
        <select
          value={current.tag_id}
          onChange={(e) => handleFilterChange({ tag_id: e.target.value })}
          className="h-10 pl-3 pr-8 rounded-lg border border-border text-sm text-primary-text bg-white focus:outline-none focus:border-primary"
          aria-label="Filter by tag"
        >
          <option value="">All Tags</option>
          {tags.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>

        {/* Hide unreachable */}
        <label className="flex items-center gap-1.5 text-xs text-secondary cursor-pointer whitespace-nowrap select-none ml-1">
          <input
            type="checkbox"
            checked={current.has_contact === 'true'}
            onChange={(e) => handleFilterChange({ has_contact: e.target.checked ? 'true' : '' })}
            className="w-3.5 h-3.5 rounded border-border accent-primary"
          />
          Hide unreachable
        </label>

        {/* Result count — right-aligned, only shown in controlled mode */}
        {resultCount !== undefined && (
          <p className="ml-auto text-xs text-secondary">
            {isUpdating
              ? 'Updating...'
              : <><span className="font-semibold text-primary-text">{resultCount.toLocaleString()}</span> results</>
            }
          </p>
        )}
      </div>

      {/* Row 2: Mode tabs */}
      <div className="flex items-stretch border-b border-border">
        {MODE_CHIPS.map((m) => (
          <button
            key={m.value}
            onClick={() => handleFilterChange({ mode: m.value })}
            className={cn(
              'flex-1 py-2 text-sm font-medium text-center transition-colors border-b-2',
              current.mode === m.value
                ? 'border-primary text-primary bg-primary/5'
                : 'border-transparent text-secondary hover:text-primary-text hover:bg-hover-bg'
            )}
          >
            {m.icon} {m.label}
          </button>
        ))}
      </div>

      {/* Row 3: Big search + quick chips */}
      <div className="px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary mb-1.5">
          Search anything — owner, unit, building, nationality
        </p>
        <form onSubmit={handleSearchSubmit}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              placeholder='e.g. "Ahmed" · "Skyz 304" · "Indian owners" · "unit 101"...'
              value={current.q}
              onChange={(e) => setCurrent({ ...current, q: e.target.value })}
              className="w-full h-12 pl-12 pr-10 rounded-xl border border-border text-sm bg-white text-primary-text placeholder:text-secondary/60 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-shadow"
              aria-label="Search landlords"
            />
            {current.q && (
              <button
                type="button"
                onClick={() => setCurrent({ ...current, q: '' })}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full text-secondary hover:text-primary-text"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>

        {/* Quick action chips */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <Link
            to="/"
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-border text-xs font-medium text-secondary hover:border-primary hover:text-primary transition-colors"
          >
            Stats
          </Link>
          <Link
            to="/buildings"
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-border text-xs font-medium text-secondary hover:border-primary hover:text-primary transition-colors"
          >
            Buildings
          </Link>
          {hasFilters && (
            <button
              onClick={handleClear}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-danger/30 text-xs font-medium text-danger hover:bg-danger/5 transition-colors"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Building cards grid — shown when area is selected and no building is chosen */}
      {current.area_id && !current.building && buildings.length > 0 && (
        <div className="px-4 py-3 border-t border-border bg-white">
          <p className="text-sm font-semibold text-primary-text mb-3">
            {areas.find((a) => String(a.id) === current.area_id)?.name ?? 'Area'}{' '}
            <span className="text-secondary font-normal">—</span>{' '}
            <span className="text-primary font-semibold">{buildings.length} buildings</span>
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
            {buildings.map((b) => (
              <button
                key={b.name}
                onClick={() => handleFilterChange({ building: b.name })}
                className="text-left px-4 py-3 rounded-xl border border-border bg-white hover:border-primary hover:shadow-md transition-all group"
              >
                <p className="text-sm font-semibold text-primary-text truncate group-hover:text-primary">{b.name}</p>
                <p className="text-xs text-primary mt-1 font-medium">{(b as any).unitCount ?? 0} units</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function buildSearchParams(state: SearchBarState): Record<string, string | undefined> {
  return {
    q: state.q || undefined,
    mode: state.mode !== 'smart' ? state.mode : undefined,
    area_id: state.area_id || undefined,
    building: state.building || undefined,
    stage: state.stage || undefined,
    tag_id: state.tag_id || undefined,
    has_contact: state.has_contact || undefined,
  };
}
